import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  ChatBubbleOutline,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import api from '../config/api';
import { PAGE_CONTAINER_MAX_WIDTH, documentPageContainerSx, documentPageCanvasSx, DOCUMENT } from '../constants/documentTheme';
import { isPackageExpired } from '../utils/partnerListUtils';
import { useSyncClientBranding } from '../contexts/ClientBrandingContext';

const formatMoney = (amount, currency = 'USD') => {
  const value = Number(amount);
  if (Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(value);
  } catch {
    return `${currency || 'USD'} ${value.toFixed(2)}`;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(String(value).includes('T') ? value : `${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusLabel = (status, t) => {
  const key = `myPackageBooking.status.${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
};

const MyPackageBookingDetail = () => {
  const { bookingRef } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [booking, setBooking] = useState(null);
  useSyncClientBranding(booking?.branding);
  const [itinerary, setItinerary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messaging, setMessaging] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/package-booking/my-bookings/${bookingRef}`);
      setBooking(response.data.booking || null);
      setItinerary(response.data.itinerary || []);
    } catch (err) {
      setError(err.response?.data?.error || t('myPackageBooking.loadFailed'));
      setBooking(null);
    } finally {
      setLoading(false);
    }
  }, [bookingRef, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openMessageThread = async () => {
    if (!booking?.booking_reference) return;
    try {
      setMessaging(true);
      if (booking.inquiry_id) {
        navigate(`/my-inquiries/${booking.inquiry_id}`);
        return;
      }
      const response = await api.post(`/package-booking/my-bookings/${booking.booking_reference}/thread`);
      const inquiryId = response.data.inquiryId;
      navigate(`/my-inquiries/${inquiryId}`);
    } catch (err) {
      setError(err.response?.data?.error || t('myPackageBooking.messageFailed'));
    } finally {
      setMessaging(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...documentPageCanvasSx, py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box sx={{ ...documentPageCanvasSx, py: 6 }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
          <Alert severity="error" sx={{ mb: 2 }}>{error || t('myPackageBooking.notFound')}</Alert>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/my-bookings')}>
            {t('myPackageBooking.back')}
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={documentPageCanvasSx}>
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/my-bookings')} sx={{ mb: 2 }}>
          {t('myPackageBooking.back')}
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 32, md: 40 }, fontWeight: 400 }}>
              {booking.package_title}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink2, mt: 0.5 }}>
              {booking.destination} · {booking.duration_days} {t('myPackageBooking.days')}
            </Typography>
            <Typography sx={{ mt: 1, fontWeight: 700, color: DOCUMENT.oxblood, letterSpacing: '0.04em' }}>
              {t('myPackageBooking.ref')}: {booking.booking_reference}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {isPackageExpired({ available_until: booking.package_available_until }) ? (
              <Chip label={t('myPackageBooking.expired')} sx={{ borderRadius: 0, bgcolor: DOCUMENT.ruleHard }} />
            ) : null}
            <Chip label={statusLabel(booking.status, t)} color="warning" sx={{ borderRadius: 0 }} />
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{t('myPackageBooking.tripDetails')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('myPackageBooking.travelDates')}</Typography>
                  <Typography>{formatDate(booking.travel_date)} → {formatDate(booking.return_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('myPackageBooking.travelers')}</Typography>
                  <Typography>
                    {booking.num_travelers} {booking.num_travelers === 1 ? t('myPackageBooking.traveler') : t('myPackageBooking.travelersLabel')}
                  </Typography>
                </Grid>
                {booking.pricing_tier && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">{t('myPackageBooking.tier')}</Typography>
                    <Typography>{booking.pricing_tier}</Typography>
                  </Grid>
                )}
                {booking.special_requests && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">{t('myPackageBooking.requests')}</Typography>
                    <Typography>{booking.special_requests}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{t('myPackageBooking.transaction')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('myPackageBooking.total')}</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{formatMoney(booking.total_price, booking.currency)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('myPackageBooking.paymentStatus')}</Typography>
                  <Typography>{booking.payment_status || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('myPackageBooking.bookedOn')}</Typography>
                  <Typography>{formatDate(booking.created_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('myPackageBooking.paidAt')}</Typography>
                  <Typography>{booking.paid_at ? formatDate(booking.paid_at) : '—'}</Typography>
                </Grid>
                {booking.partner_confirmation_id && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">{t('myPackageBooking.agentConfirmation')}</Typography>
                    <Typography sx={{ fontWeight: 600, color: DOCUMENT.oxblood }}>{booking.partner_confirmation_id}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {itinerary.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{t('myPackageBooking.itinerary')}</Typography>
                {itinerary.map((item) => (
                  <Box key={item.id} sx={{ py: 1.25, borderTop: `1px solid ${DOCUMENT.rule}` }}>
                    <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {t('myPackageBooking.day')} {item.day_number}
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>{item.title || item.name}</Typography>
                    {item.description && (
                      <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                    )}
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{t('myPackageBooking.agent')}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{booking.partner_name}</Typography>
              {booking.partner_contact_name && (
                <Typography variant="body2">{booking.partner_contact_name}</Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Button
                  variant="contained"
                  startIcon={<ChatBubbleOutline />}
                  onClick={openMessageThread}
                  disabled={messaging}
                >
                  {messaging ? t('myPackageBooking.openingMessages') : t('myPackageBooking.messageAgent')}
                </Button>
                {booking.payment_link_url && ['partner_confirmed', 'payment_pending'].includes(booking.status) && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<PaymentIcon />}
                    onClick={() => window.open(booking.payment_link_url, '_blank')}
                  >
                    {t('myPackageBooking.payNow')}
                  </Button>
                )}
                {(booking.trip_id || booking.package_slug) && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(booking.trip_id
                      ? `/trips/${booking.trip_id}`
                      : `/packages/${booking.package_slug}`)}
                  >
                    {t('myPackageBooking.viewItinerary')}
                  </Button>
                )}
              </Box>
            </Paper>
            {booking.partner_notes && (
              <Alert severity="info">
                <strong>{t('myPackageBooking.agentNotes')}:</strong> {booking.partner_notes}
              </Alert>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default MyPackageBookingDetail;
